import { effect, inject, signal } from '@angular/core';
import type { UrlTree } from '@angular/router';
import { type CanActivateFn, type GuardResult, Router } from '@angular/router';

import { ProductAdminContext } from '#/app/shared/services/product-admin.context';
import { TenantContext } from '#/app/shared/services/tenant.context';

/**
 * Redirects from /o based on the current user's tenant memberships:
 *  - 0 tenants (if not product admin)  -> /no-access
 *  - 0 tenants (if product admin)  -> /admin/organizations
 *  - 1 tenant   -> /o/{tenantId}
 *  - >1 tenants -> /o
 */
export const tenantLandingGuard: CanActivateFn = () => {
  const router = inject(Router);
  const productAdminContext = inject(ProductAdminContext);
  const tenantContext = inject(TenantContext);
  const reloadTenantsListInitiated = signal(false);

  return new Promise<GuardResult>((resolve) => {
    const effectRef = effect(
      () => {
        if (tenantContext.isTenantsLoading()) {
          return;
        }

        if (!reloadTenantsListInitiated()) {
          tenantContext.tenants.reload();
          reloadTenantsListInitiated.set(true);
          return;
        }

        if (productAdminContext.hasCurrentProductUserLoaded()) {
          const tenantsResource = tenantContext.tenants;
          if (tenantsResource.status() === 'error') {
            effectRef.destroy();
            resolve(router.createUrlTree(['/no-access']));
          }
          if (tenantsResource.hasValue()) {
            const tenants = tenantsResource.value();
            let target: UrlTree | true;
            if (tenants.length === 0) {
              target = productAdminContext.isProductUser()
                ? router.createUrlTree(['/admin/organizations'])
                : router.createUrlTree(['/no-access']);
            } else if (tenants.length === 1) {
              target = router.createUrlTree([`/o/${tenants[0].tenantId}`]);
            } else {
              target = true;
            }
            effectRef.destroy();
            resolve(target);
          }
        }
      },
      { manualCleanup: true },
    );
  });
};
